import DoospLayout from "@/components/doosp/DoospLayout";
import { Link } from "react-router-dom";
import { DOOSP_INFO } from "@/lib/programs";
import { ShieldCheck, Globe2, Target, Users, ArrowRight, Award, Microscope, BookOpen } from "lucide-react";

export default function About() {
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">About {DOOSP_INFO.short}</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="about-heading">
            An independent<br />
            <span className="text-red-800">non-profit competency body.</span>
          </h1>
          <p className="text-lg text-zinc-600 mt-8 leading-relaxed max-w-3xl">
            The <strong>Department of Occupational Safety Programs (DOOSP)</strong> is a specialty
            division of the <strong>International Medical Competency Accreditation Council (IMCAC)</strong>,
            established to develop structured Occupational Safety Programs and competency-based
            assessment frameworks for healthcare, workplace safety, and other safety-critical
            environments.
          </p>
          <p className="text-base text-zinc-600 mt-5 leading-relaxed max-w-3xl">
            DOOSP focuses on competency development, assessment, and professional credentialing.
            Successful candidates may receive a verifiable DOOSP professional competency certificate
            documenting their achievement against the requirements of the applicable program.
          </p>
          <p className="text-sm text-zinc-500 mt-5 leading-relaxed max-w-3xl">
            IMCAC and DOOSP operate independently of government licensing and regulatory
            authorities. They do not grant statutory licenses to practice, professional registration,
            academic degrees, or government authorization. DOOSP certification does not replace any
            registration, licensing, statutory authorization, or other legal requirement applicable
            to the individual&rsquo;s profession or jurisdiction.
          </p>
        </div>
      </section>

      {/* §3 — What is an Occupational Safety Program */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-3">§3 &middot; Definition</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            What is an Occupational Safety Program?
          </h2>
          <p className="text-zinc-600 mt-6 leading-relaxed max-w-3xl">
            An Occupational Safety Program is a structured education, training, risk-management,
            and competency-assessment framework designed to help individuals and organizations
            identify, prevent, manage, and respond to workplace hazards and safety risks.
          </p>
          <p className="text-sm text-zinc-500 mt-4 max-w-3xl">Occupational Safety Programs may address areas such as:</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {[
              "Workplace hazard identification & risk assessment",
              "Infection prevention & control",
              "Occupational exposure prevention",
              "Sharps & needle-stick injury prevention",
              "Personal protective equipment (PPE)",
              "Chemical & biological safety",
              "Biomedical & hazardous waste management",
              "Fire & emergency preparedness",
              "Equipment & workplace safety",
              "Environmental & facility safety",
              "Incident reporting & response",
              "Emergency procedures",
              "Occupational health & safety awareness",
              "Safe operating procedures",
              "Workplace safety documentation & SOPs",
              "Risk prevention & safety culture",
              "Role-specific safety competencies",
            ].map((topic) => (
              <div key={topic} className="border border-zinc-200 rounded-md px-3 py-2.5 text-sm text-zinc-700 bg-zinc-50">
                {topic}
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-6 italic max-w-3xl">
            The exact content always depends on the scope of the individual DOOSP program.
          </p>
        </div>
      </section>

      {/* §4 — What DOOSP Does */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-3">§4 &middot; Scope of activity</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">What DOOSP does</h2>
          <p className="text-zinc-600 mt-6 leading-relaxed max-w-3xl">
            DOOSP programs are designed to establish and assess defined occupational safety
            competencies. Depending on the program, DOOSP may:
          </p>
          <ul className="mt-6 space-y-2.5 max-w-3xl">
            {[
              "Define competency standards.",
              "Develop structured learning outcomes.",
              "Develop occupational safety training frameworks.",
              "Establish assessment criteria.",
              "Conduct or administer competency-based assessments.",
              "Evaluate participant performance against defined criteria.",
              "Issue verifiable certificates to individuals who successfully meet the applicable requirements.",
              "Maintain appropriate verification mechanisms for issued credentials.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-zinc-700 leading-relaxed">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-red-800 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500 mt-6 italic max-w-3xl">
            DOOSP uses the term <strong>Competency-Based Assessment</strong>. DOOSP does not grant
            a statutory professional license.
          </p>
        </div>
      </section>

      {/* §5 — What a DOOSP certificate means */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-3">§5 &middot; The credential</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            What a DOOSP certificate means
          </h2>
          <p className="text-zinc-600 mt-6 leading-relaxed max-w-3xl">
            A DOOSP certificate confirms that the holder has successfully completed the applicable
            DOOSP program requirements and/or competency-based assessment specified for that
            credential. Each certificate is issued as a verifiable digital PDF credential and may
            be independently verified through the designated verification system.
          </p>
          <div className="mt-8 border-l-4 border-red-800 bg-red-50 px-5 py-4 rounded-r-md max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-widest text-red-900 mb-2">A DOOSP certificate is not:</div>
            <ul className="text-sm text-red-950 space-y-1.5">
              {[
                "A government license",
                "A statutory license",
                "A medical license",
                "A professional registration",
                "A government qualification",
                "A university degree",
                "A legally required authorization to practice",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-red-700 font-bold">✕</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-red-900 mt-3 italic">
              …unless a specific credential has separately received such recognition from the
              relevant authority.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12">
          <div>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-red-50 mb-4">
              <Target className="h-6 w-6 text-red-800" />
            </div>
            <h2 className="font-display text-2xl font-bold">Mission</h2>
            <p className="text-zinc-600 mt-3 leading-relaxed">
              To deliver evidence-based, globally aligned certification programs that
              measurably reduce occupational injury, exposure, and illness across
              healthcare and adjacent industries.
            </p>
          </div>
          <div>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-red-50 mb-4">
              <Globe2 className="h-6 w-6 text-red-800" />
            </div>
            <h2 className="font-display text-2xl font-bold">Vision</h2>
            <p className="text-zinc-600 mt-3 leading-relaxed">
              A world in which every healthcare worker and safety-critical professional
              has instant access to independently-assessed, current, and portable competency
              credentials — regardless of geography or organization.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl font-bold text-center">Our approach</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { icon: Microscope, title: "Evidence-based curricula", body: "Every module cites primary sources — OSHA standards, CDC guidance, WHO recommendations, and peer-reviewed literature." },
              { icon: Users, title: "Practitioner-led review", body: "All content is authored and reviewed by working clinicians, safety officers, and industrial hygienists." },
              { icon: BookOpen, title: "Continuous updates", body: "New guidance triggers a course update within the certification cycle — your credential stays current." },
            ].map((v) => (
              <div key={v.title} className="bg-white rounded-lg p-6 border border-zinc-200">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-red-50 mb-4">
                  <v.icon className="h-5 w-5 text-red-800" />
                </div>
                <div className="font-display font-bold text-lg">{v.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <h2 className="font-display text-3xl font-bold">Curricula aligned to</h2>
          <p className="text-sm text-zinc-500 mt-1">Our assessments cover competencies drawn from these primary sources. Alignment reflects content coverage — it is not an endorsement, accreditation, or recognition by any of these bodies.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {[
              { code: "OSHA 29 CFR 1910.1030", label: "Bloodborne Pathogens" },
              { code: "OSHA 29 CFR 1910.132", label: "PPE — General Requirements" },
              { code: "CDC / HICPAC", label: "Isolation Precautions" },
              { code: "WHO IPC guidelines", label: "Infection Prevention & Control" },
              { code: "ANSI Z136.3", label: "Medical Laser Safety" },
              { code: "EPA / RCRA", label: "Medical Waste Regulation" },
            ].map((s) => (
              <div key={s.code} className="border border-zinc-200 rounded-md p-4 bg-zinc-50">
                <div className="text-[11px] font-mono uppercase tracking-widest text-red-800">{s.code}</div>
                <div className="font-medium text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-r from-red-800 to-red-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">
            Explore our certification programs.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/programs" className="bg-white text-red-800 hover:bg-red-50 h-11 px-6 inline-flex items-center gap-2 rounded-md text-sm font-medium">
              See all programs <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="border-2 border-white/40 hover:bg-white/10 text-white h-11 px-6 inline-flex items-center rounded-md text-sm font-medium">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </DoospLayout>
  );
}
